import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  CommonRegEx,
  DialogService,
  EntityCacheProvider,
  FormatService,
  LmapService,
  TestAssembleService,
  NotificationService,
  TestServerConfigService,
  TransformationService,
  IDeviceData,
  Device,
  ValuesExchangeProvider,
  RunTypeValue
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { get } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VoipConfigMapping as ConfigFieldMapping } from './json/voip-agent-model';

@Injectable({
  providedIn: 'root'
})
export class VoipTestService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  exceedsIPRange: boolean;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  formatService: FormatService;
  testAssembleService: TestAssembleService;
  lmapService: LmapService;
  serverConfigService: TestServerConfigService;
  controllerConfig = null;
  no_proxy = '{"sip-proxy":"No Proxy Selected"}';
  private CODEC_URL = '/monitor/MonitorManager/v1/codecs';
  private getControllerConfig = '/uicomm/getControllerConfiguration';
  private readonly unsubscribe: Subject<void> = new Subject();
  runType = RunTypeValue.ON_GOING;
  deviceModel = 'vTA';
  testTaskName = 'voip';
  tenantId = 'paas.local';
  originator: Device;
  destination: Device;
  generatedId: string;

  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    formatService: FormatService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notifyService: NotificationService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.testAssembleService = testAssmebleService;
    this.formatService = formatService;
    this.lmapService = lmapService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadCodecOptions(callbackFn: (any) => any): void {
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(this.CODEC_URL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          callbackFn(response);
        },
        error => {
          callbackFn(error);
        },
        () => {}
      );
  }

  getControllerConfiguration(): void {
    const url = `${this.getControllerConfig}`;
    this.controllerConfig = this.apiService.post$<any>(url);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  getProxyConfig(config: any): any {
    const sipProxy = {
      'sip-proxy': {
        authentication: {
          password: config.password,
          name: config.sign_in
        },
        alias: config.user,
        'nat-traversal': {
          'stun-server': '',
          'turn-user': '',
          'turn-password': ''
        },
        'proxy-server-config': {
          address: config.address,
          port: config.udpPort,
          transport: 'udp'
        }
      }
    };
    return JSON.stringify(sipProxy);
  }
  isValidSipPhoneNumber(sipPhoneNumber: string): boolean {
    const phoneNumber = /^\(?\+?[\d(\-\s)]+$/;
    let result = false;
    if (sipPhoneNumber.includes('@')) {
      const sipIp = sipPhoneNumber.split('@')[1];
      result = CommonRegEx.IP_V4_V6.test(sipIp);
    }
    if (!result) {
      if (
        CommonRegEx.EMAIL.test(sipPhoneNumber.toLowerCase()) ||
        phoneNumber.test(sipPhoneNumber)
      ) {
        if (sipPhoneNumber.includes('@')) {
          const sipIp = sipPhoneNumber.split('@')[1];
          const formatSipIp = sipIp.replace(/\./g, '');
          return !isNaN(Number(formatSipIp))
            ? CommonRegEx.IP_V4_V6.test(sipPhoneNumber.split('@')[1])
            : formatSipIp.includes(':')
            ? false
            : true;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }
    return result;
  }

  //eslint-disable-next-line
  createLMAPObject(): any {
    this.runType = this.output.runType;
    const testPointA = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    this.generatedId = this.serverConfigService.generateUUID();
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL);
    //const events = this.createEvents(testSchedule);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    return controllerConfig;
  }

  //eslint-disable-next-line
  createControllerConfig(testPointA, testPointB, lmapified): any {
    let controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );

    const allDestTestParams = {};
    allDestTestParams['voip'] = this.getAllDestinationTestParams();

    const secondaryConfig = JSON.parse(JSON.stringify(lmapified));
    // eslint-disable-next-line prefer-const
    let foundRole = false,
      foundPhyId = false;
    // secondary config is created as a copy of primary config, so need to update for role and phy-id
    for (const x in secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0]
      .option) {
      secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].tag =
        this.getTagSecondaryInfo();
      secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[0].value =
        JSON.stringify(allDestTestParams);
      // remove phy-id object if secondary device doesn't have multiple ports
      if (!foundPhyId && !testPointB?.phyId) {
        if (
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
          'phy-id'
        ) {
          foundPhyId = true;
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.splice(
            x,
            1
          );
        }
      }

      // set the value of phy-id
      if (
        !foundPhyId &&
        secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
          'phy-id'
      ) {
        secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].value =
          testPointB?.phyId ? testPointB?.phyId : ''; // set value if exists, blank otherwise
        foundPhyId = true;
      }
      if (foundRole && foundPhyId) {
        break;
      }
    }

    // case when primary device is single port, so need to add phy-id object for secondary config
    if (!foundPhyId) {
      if (testPointB?.phyId) {
        const optionObjIndex =
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.length;
        secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
          optionObjIndex
        ] = {};
        secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
          optionObjIndex
        ].id = 'phy-id';
        secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
          optionObjIndex
        ].value = testPointB?.phyId;
      }
    }

    controllerConfig = this.testAssembleService.addSecondaryTestConfig(
      controllerConfig,
      'secondaryConfig',
      testPointB,
      this.testTaskName,
      secondaryConfig
    );
    controllerConfig.test['test-list-data']['runType'] = this.runType;
    if (get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')) {
      controllerConfig.test['test-list-data']['tpInfo'].push({
        id: get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber'),
        type: 'secondary',
        model: 'phonenumber'
      });
    }
    controllerConfig.test['test-list-data']['proxyConfig'] = this.createProxyConfig();
    controllerConfig.test['test-list-data']['threshold'] = {
      critical: this.output?.thresholdCritical,
      warning: this.output?.thresholdWarning
    };
    return controllerConfig;
  }

  createProxyConfig(): any {
    const proxyConfig = [
      {
        key: 'voip.codec',
        value: this.output.codec
      },
      {
        key: 'sip-proxy-originator',
        value:
          this.output?.originator_proxy?.type === 'custom'
            ? this.getProxyConfig(this.output.originator_proxy)
            : this.no_proxy
      },
      {
        key: 'sip-proxy-destination',
        value:
          this.output?.destination_proxy?.type === 'custom'
            ? this.getProxyConfig(this.output.destination_proxy)
            : this.no_proxy
      }
    ];

    return proxyConfig;
  }

  //eslint-disable-next-line
  createTestSchedule(testPointA: any, collectorURL: string): any {
    // create the test schedule
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    const testEventName = 'immediate';
    // const optionArray = this.getTestActionOptions(deviceData);
    const allTestParams = {};
    allTestParams['voip'] = this.getAllOriginTestParams();

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };

    const actionOption2 = {
      id: 'role',
      value: 'primary'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption2);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    const tags = this.getTagInfo();
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);

    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'pm',
      collectorURL,
      this.output.interval,
      null
    );
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    return lmapified;
  }

  getTestActionOptions(deviceData: IDeviceData): Array<{ id: string; value: string }> {
    const optionArray = [];

    const allTestParams = {};
    allTestParams['voip'] = this.getAllOriginTestParams();
    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };
    optionArray.push(actionOption);
    optionArray.push({ id: 'role', value: 'primary' });
    return optionArray;
  }

  getTagInfo(): Array<string> {
    const tags = [];
    tags.push('service-name:VoIP');
    tags.push('monitor-type:' + this.runType);
    tags.push('tenant-id:paas.local');
    tags.push(
      'dst-agent-id:' +
        (get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          ? null
          : this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.getDevice().id)
    );
    tags.push('voip-direction:' + 'caller');
    tags.push(
      'voip-sip-id-phonenumber:' +
        (get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          ? get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          : null)
    );
    return tags;
  }
  getTagSecondaryInfo(): Array<string> {
    const tags = [];
    tags.push('service-name:VoIP');
    tags.push('monitor-type:' + this.runType);
    tags.push('tenant-id:paas.local');
    tags.push(
      'dst-agent-id:' +
        (get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          ? null
          : this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.getDevice().id)
    );
    tags.push('voip-direction:' + 'answer');
    tags.push(
      'voip-sip-id-phonenumber:' +
        (get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          ? get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          : null)
    );
    return tags;
  }

  getAllOriginTestParams(): any {
    const isCustom = this.output?.originator_proxy?.type === 'custom';
    const sipConfig = isCustom
      ? {
          'sip-config': {
            'proxy-server-config': {
              address: this.output?.originator_proxy?.address,
              port: this.output?.originator_proxy?.udpPort,
              transport: 'udp'
            },
            authentication: {
              name: this.output?.originator_proxy?.sign_in,
              password: this.output?.originator_proxy?.password
            }
          }
        }
      : null;

    return {
      config: this._getConfigOriginParameters(sipConfig)
    };
  }

  getAllDestinationTestParams(): any {
    const isCustom = this.output?.destination_proxy?.type === 'custom';
    const sipConfig = isCustom
      ? {
          'sip-config': {
            'proxy-server-config': {
              address: this.output?.destination_proxy?.address,
              port: this.output?.destination_proxy?.udpPort,
              transport: 'udp'
            },
            authentication: {
              name: this.output?.destination_proxy?.sign_in,
              password: this.output?.destination_proxy?.password
            }
          }
        }
      : null;

    return {
      config: this._getConfigDestParameters(sipConfig)
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  _getConfigOriginParameters(sipConfig: any): any {
    const interval = this.runType === RunTypeValue.ON_GOING ? 0 : 480;
    return {
      action: 'place-call',
      interval: 60,
      'number-of-intervals': interval,
      'call-setup': {
        'device-id': this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].getDevice().ipaddress, //'10.15.14.123',
        'call-party': 'calling',
        'call-management-config': {
          alias: this.output?.originator_proxy?.sign_in
            ? this.output.originator_proxy.sign_in
            : 'ma1',
          'voip-test-management': {
            type: 'twoway',
            duration: 50
          }
        },
        'called-party-setup': {
          alias: this.output?.destination_proxy?.user
            ? this.output.destination_proxy.user
            : get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
            ? get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
            : this.generatedId +
              '@' +
              this.output[ConfigFieldMapping.SELECTED_DESTINATION].getDevice().ipaddress
        },
        'call-config': {
          'qos-value': 184, //hard coded
          'rtp-header-config': {
            //hard coded
            'payload-type': 0,
            'sequence-number': 0
          },
          'signaling-config': {
            'qos-value': 160, //hard coded
            ...sipConfig
          },
          payload: {
            size: this.output.codec == '4' || this.output.codec == '5' ? 30 : 20 //Based on voip codec
          },
          'rtp-port': {
            //hard coded
            low: 55000,
            high: 57000
          },
          'use-rtcp': true //hard coded
        }
      }
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  _getConfigDestParameters(sipConfig: any): any {
    const interval = this.runType === RunTypeValue.ON_GOING ? 0 : 480;
    return {
      action: 'answer-mode',
      interval: 60,
      'number-of-intervals': interval,
      'call-setup': {
        'device-id': !get(this.output[ConfigFieldMapping.SELECTED_DESTINATION], 'phonenumber')
          ? this.output[ConfigFieldMapping.SELECTED_DESTINATION].getDevice().ipaddress
          : '', //'10.15.14.123',
        'call-party': 'called',
        'call-management-config': {
          alias: this.output?.destination_proxy?.sign_in
            ? this.output.destination_proxy.sign_in
            : this.generatedId,
          'voip-test-management': {
            type: 'twoway',
            duration: 50
          }
        },
        'called-party-setup': null,
        'call-config': {
          'qos-value': 184, //hard coded
          'rtp-header-config': {
            //hard coded
            'payload-type': 0,
            'sequence-number': 0
          },
          'signaling-config': {
            'qos-value': 160, //hard coded
            ...sipConfig
          },
          payload: {
            size: this.output.codec == '4' || this.output.codec == '5' ? 30 : 20 //Based on voip codec
          },
          'rtp-port': {
            //hard coded
            low: 55000,
            high: 57000
          },
          'use-rtcp': true //hard coded
        }
      }
    };
  }
}
